import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Note, CaseFile, UserData } from '../types';
import { db } from '../services/databaseService';
import { generateLegalDraft, refineLegalText } from '../services/geminiService';
import { RateLimitService } from '../services/rateLimitService';
import { useDebounce } from '../hooks/useDebounce';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import {
  Plus, Search, FileText, Trash2, Download, Wand2,
  AlignLeft, Type, BookOpen, FileEdit, Zap
} from 'lucide-react';
import PromptModal from './PromptModal';

const DRAFT_TYPES = [
  { id: 'contract_rent', label: 'Contract Închiriere', icon: '📄' },
  { id: 'notice', label: 'Notificare Prealabilă', icon: '📢' },
  { id: 'intampinare', label: 'Draft Întâmpinare', icon: '⚖️' },
  { id: 'power_of_attorney', label: 'Împuternicire', icon: '🖋️' },
  { id: 'custom', label: 'Document Personalizat', icon: '✨' }
];

interface AiEditorViewProps {
  notes: Note[];
  cases: CaseFile[];
  user: UserData;
  onAddNote: (note: Omit<Note, 'id' | 'createdAt'>) => Promise<Note | undefined>;
  onUpdateNote: (id: string, updates: Partial<Note>) => void;
  onDeleteNote: (id: string) => void;
}

const AiEditorView: React.FC<AiEditorViewProps> = ({
  notes, cases, user, onAddNote, onUpdateNote, onDeleteNote
}) => {
  const location = useLocation();
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Starea editorului curent
  const [currentTitle, setCurrentTitle] = useState('');
  const [currentContent, setCurrentContent] = useState('');
  const [currentCaseId, setCurrentCaseId] = useState('');

  const [isGenerating, setIsGenerating] = useState(false);
  const [showAiMenu, setShowAiMenu] = useState(false);
  const [aiMenuPos, setAiMenuPos] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState('');

  // Prompt Modal state
  const [promptModalConfig, setPromptModalConfig] = useState<{
    isOpen: boolean;
    typeId: string;
    typeLabel: string;
    initialContext?: string;
  }>({ isOpen: false, typeId: '', typeLabel: '' });

  const editorRef = useRef<HTMLTextAreaElement>(null);

  // Auto-save logic
  const debouncedContent = useDebounce(currentContent, 1500);
  const debouncedTitle = useDebounce(currentTitle, 1500);
  const debouncedCaseId = useDebounce(currentCaseId, 1500);

  // Check for prefillContext from navigation state
  useEffect(() => {
    const state = location.state as { prefillContext?: string };
    if (state?.prefillContext) {
      setPromptModalConfig({
        isOpen: true,
        typeId: 'custom',
        typeLabel: 'Document Personalizat',
        initialContext: state.prefillContext
      });
      
      // Clear the state so it doesn't reopen on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const selectedNote = notes.find(n => n.id === selectedNoteId);

  // Incarcă nota selectată
  useEffect(() => {
    if (selectedNote) {
      setCurrentTitle(selectedNote.title);
      setCurrentContent(selectedNote.content);
      setCurrentCaseId(selectedNote.linkedCaseId || '');
    } else {
      setCurrentTitle('');
      setCurrentContent('');
      setCurrentCaseId('');
    }
  }, [selectedNoteId]);

  // Salvare automată când debounced values se schimbă
  useEffect(() => {
    if (selectedNoteId && selectedNote) {
      // Verificăm dacă chiar s-a modificat ceva față de sursă pentru a evita call-uri inutile
      if (
        debouncedTitle !== selectedNote.title || 
        debouncedContent !== selectedNote.content ||
        debouncedCaseId !== (selectedNote.linkedCaseId || '')
      ) {
        onUpdateNote(selectedNoteId, {
          title: debouncedTitle,
          content: debouncedContent,
          linkedCaseId: debouncedCaseId
        });
      }
    }
  }, [debouncedTitle, debouncedContent, debouncedCaseId, selectedNoteId]);

  const handleCreateNew = async () => {
    const defaultTitle = `Document Nou ${new Date().toLocaleDateString('ro-RO')}`;
    
    const newNote = await onAddNote({
      title: defaultTitle,
      content: '',
      linkedCaseId: ''
    });

    if (newNote) {
      setSelectedNoteId(newNote.id);
      setCurrentTitle(newNote.title);
      setCurrentContent(newNote.content);
    }
  };

  const handleGenerateTemplate = async (typeId: string) => {
    const isAllowed = await RateLimitService.checkDailyLimit('aiCalls');
    if (!isAllowed) {
      toast.error(`Limită AI zilnică atinsă (protecție beta)`);
      return;
    }

    const typeLabel = DRAFT_TYPES.find(t => t.id === typeId)?.label || 'Document';
    setPromptModalConfig({
      isOpen: true,
      typeId,
      typeLabel
    });
  };

  const handlePromptSubmit = async (contextInfo: string) => {
    const { typeId, typeLabel } = promptModalConfig;
    if (!contextInfo || !typeId) return;

    setIsGenerating(true);
    try {
      RateLimitService.incrementUsage('aiCalls');
      const draft = await generateLegalDraft(typeLabel, contextInfo);

      const title = `${typeLabel} - ${new Date().toLocaleDateString('ro-RO')}`;

      const newNote = await onAddNote({
        title: title,
        content: draft,
        linkedCaseId: ''
      });

      if (newNote) {
        setSelectedNoteId(newNote.id);
        toast.success("Draft generat cu succes!");
      }
    } catch (err: any) {
      toast.error("Eroare generare", { description: err.message });
    } finally {
      setIsGenerating(false);
      setPromptModalConfig({ isOpen: false, typeId: '', typeLabel: '' });
    }
  };

  const handleTextSelection = () => {
    if (!editorRef.current) return;
    
    const textarea = editorRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    if (start !== end) {
      const text = textarea.value.substring(start, end);
      setSelectedText(text);
    } else {
      setSelectedText('');
      setShowAiMenu(false);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (selectedText) {
      e.preventDefault();
      setAiMenuPos({ x: e.clientX, y: e.clientY });
      setShowAiMenu(true);
    }
  };

  const handleAiRefinement = async (action: 'formal' | 'explain' | 'translate_en' | 'summarize' | 'expand') => {
    if (!selectedText) return;
    if (!RateLimitService.checkDailyLimit('aiCalls')) {
      toast.error(`Limită AI zilnică atinsă (protecție beta)`);
      return;
    }

    setShowAiMenu(false);
    
    const loadingToast = toast.loading("AI procesează textul...");
    try {
      RateLimitService.incrementUsage('aiCalls');
      const refinedText = await refineLegalText(selectedText, action);
      
      // Înlocuiește textul selectat
      const textarea = editorRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newContent = currentContent.substring(0, start) + refinedText + currentContent.substring(end);
        setCurrentContent(newContent);
        
        // Selectează noul text
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start, start + refinedText.length);
        }, 100);
      }
      
      toast.success("Text îmbunătățit!", { id: loadingToast });
    } catch (err: any) {
      toast.error("Eroare AI", { description: err.message, id: loadingToast });
    }
  };

  const exportToDocx = async () => {
    if (!currentTitle || !currentContent) return;
    
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            children: [
              new TextRun({ text: currentTitle, bold: true, size: 32 })
            ],
            spacing: { after: 400 }
          }),
          ...currentContent.split('\n').map(text => 
            new Paragraph({
              children: [new TextRun({ text, size: 24 })],
              spacing: { after: 200 }
            })
          )
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${currentTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`);
    toast.success("Document exportat cu succes!");
  };

  const filteredNotes = notes.filter(n => 
    n.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    n.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-[calc(100vh-6rem)] md:h-[calc(100vh-8rem)] bg-slate-50 overflow-hidden animate-in fade-in rounded-3xl border border-slate-100 shadow-sm mx-4 md:mx-8 mb-8">
      
      {/* Sidebar - Lista de Documente */}
      <div className={`w-full md:max-w-[320px] bg-white md:border-r border-slate-200 flex-col shrink-0 z-10 ${selectedNoteId ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-6 border-b border-slate-100 shrink-0">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-black text-slate-900 italic tracking-tight">Editor AI</h2>
            <button 
              onClick={handleCreateNew}
              className="w-10 h-10 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200 transition-all active:scale-95"
              title="Document Nou"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input 
              type="text" 
              placeholder="Caută documente..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 border border-slate-100 rounded-xl pl-10 pr-4 py-3 text-xs font-bold text-slate-700 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-400"
            />
          </div>
        </div>

        {/* Quick Actions (Mobile Only) */}
        <div className="md:hidden px-4 pb-2 border-b border-slate-100 shrink-0">
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1 mt-2">Generare AI Rapidă</p>
          <div className="flex gap-2 overflow-x-auto pb-3 pt-1 px-1 custom-scrollbar snap-x">
            {DRAFT_TYPES.map(type => (
              <button
                key={type.id}
                onClick={() => handleGenerateTemplate(type.id)}
                className="flex flex-col items-center justify-center gap-2 p-3 min-w-[110px] rounded-2xl bg-indigo-50/50 hover:bg-indigo-50 border border-indigo-100 snap-start active:scale-95 transition-all"
              >
                <span className="text-2xl">{type.icon}</span>
                <span className="text-[8px] font-black text-indigo-900 uppercase tracking-wider text-center">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2">
          {filteredNotes.length === 0 ? (
            <div className="p-6 text-center text-slate-400 font-medium text-sm italic">
              Niciun document găsit.
            </div>
          ) : (
            filteredNotes.map(note => (
              <div 
                key={note.id}
                onClick={() => setSelectedNoteId(note.id)}
                className={`p-4 rounded-2xl cursor-pointer transition-all border ${
                  selectedNoteId === note.id 
                    ? 'bg-indigo-50 border-indigo-200 shadow-sm' 
                    : 'bg-white border-transparent hover:bg-slate-50 hover:border-slate-200'
                }`}
              >
                <h4 className={`font-black text-sm truncate mb-1 ${selectedNoteId === note.id ? 'text-indigo-900' : 'text-slate-700'}`}>
                  {note.title || 'Fără titlu'}
                </h4>
                <p className="text-[10px] text-slate-400 font-medium line-clamp-2 leading-relaxed">
                  {note.content || 'Niciun conținut...'}
                </p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    {new Date(note.createdAt).toLocaleDateString('ro-RO')}
                  </span>
                  {selectedNoteId === note.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeleteNote(note.id); if(selectedNoteId === note.id) setSelectedNoteId(null); }}
                      title="Șterge documentul"
                      aria-label="Șterge documentul"
                      className="text-rose-400 hover:text-rose-600 p-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Editor Area */}
      <div className={`flex-1 flex flex-col relative bg-white ${!selectedNoteId ? 'hidden md:flex' : 'flex'}`}>
        {selectedNoteId ? (
          <>
            {/* Toolbar Top */}
            <div className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 shrink-0 z-10 gap-2 md:gap-4">
              <button 
                onClick={() => setSelectedNoteId(null)}
                className="md:hidden p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                aria-label="Înapoi la listă"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
              </button>
              
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={currentTitle}
                  onChange={(e) => setCurrentTitle(e.target.value)}
                  placeholder="Titlu document..."
                  className="w-full text-base md:text-lg font-black text-slate-900 bg-transparent border-none outline-none focus:ring-0 text-slate-700 placeholder:text-slate-300 placeholder:italic"
                />
              </div>

              <div className="flex items-center gap-2 md:gap-3 shrink-0">
                <select
                  value={currentCaseId}
                  onChange={(e) => setCurrentCaseId(e.target.value)}
                  title="Asociază un dosar"
                  aria-label="Asociază un dosar"
                  className="hidden sm:block bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 md:px-3 md:py-2 text-[10px] md:text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 max-w-[120px] md:max-w-xs"
                >
                  <option value="" className="text-slate-900">Fără Dosar</option>
                  {cases.map(c => <option key={c.id} value={c.id} className="text-slate-900">{c.caseNumber}</option>)}
                </select>

                <button
                  onClick={exportToDocx}
                  title="Export în format DOCX"
                  aria-label="Export în format DOCX"
                  className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-[9px] md:text-xs font-black uppercase tracking-wider transition-all shadow-md active:scale-95"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export .DOCX</span>
                  <span className="sm:hidden">.DOCX</span>
                </button>
              </div>
            </div>

            {/* AI Floating Context Menu */}
            {showAiMenu && selectedText && (
              <div 
                className="fixed bg-white rounded-xl shadow-2xl border border-indigo-100 p-2 z-[200] w-64 animate-in fade-in zoom-in-95 duration-200"
                // eslint-disable-next-line react/forbid-dom-props
                /* nosonar */ style={{ top: Math.min(aiMenuPos.y + 10, window.innerHeight - 250), left: Math.min(aiMenuPos.x + 10, window.innerWidth - 300) }}
              >
                <div className="px-3 py-2 border-b border-slate-100 mb-2">
                  <p className="text-[9px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-1.5">
                    <Wand2 className="w-3 h-3" /> Asistent AI LexSmart
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <button 
                    onClick={() => handleAiRefinement('formal')} 
                    title="Rescrie textul într-un limbaj juridic formal"
                    aria-label="Rescrie formal"
                    className="text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <BookOpen className="w-3.5 h-3.5" /> Rescrie Formal (Juridic)
                  </button>
                  <button 
                    onClick={() => handleAiRefinement('explain')} 
                    title="Explică textul într-un limbaj simplu pentru client"
                    aria-label="Explică simplu"
                    className="text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-amber-50 hover:text-amber-700 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <AlignLeft className="w-3.5 h-3.5" /> Explică Simplu (Pentru Client)
                  </button>
                  <button 
                    onClick={() => handleAiRefinement('expand')} 
                    title="Dezvoltă argumentația pe baza textului selectat"
                    aria-label="Dezvoltă argumentația"
                    className="text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Type className="w-3.5 h-3.5" /> Dezvoltă Argumentația
                  </button>
                  <button 
                    onClick={() => handleAiRefinement('summarize')} 
                    title="Rezumă textul selectat"
                    aria-label="Rezumă textul"
                    className="text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <FileEdit className="w-3.5 h-3.5" /> Rezumă
                  </button>
                </div>
                
                <button 
                  onClick={() => setShowAiMenu(false)} 
                  title="Închide meniul AI"
                  aria-label="Închide meniul"
                  className="mt-2 w-full text-center py-2 text-[10px] font-bold text-slate-400 hover:text-slate-600"
                >
                  Anulează
                </button>
              </div>
            )}

            {/* Click outside menu hider */}
            {showAiMenu && (
              <div 
                className="fixed inset-0 z-[190]" 
                onClick={() => setShowAiMenu(false)}
                onContextMenu={(e) => { e.preventDefault(); setShowAiMenu(false); }}
              />
            )}

            {/* Editor Textarea */}
            <div className="flex-1 p-4 md:p-8 lg:p-16 overflow-y-auto bg-slate-50/30">
              <div className="max-w-3xl mx-auto min-h-full bg-white shadow-xl shadow-slate-200/50 p-6 md:p-12 rounded-[2rem] border border-slate-100 flex flex-col">
                <textarea
                  ref={editorRef}
                  value={currentContent}
                  onChange={(e) => setCurrentContent(e.target.value)}
                  onSelect={handleTextSelection}
                  onContextMenu={handleContextMenu}
                  placeholder="Începe să scrii aici... Selectează un text și fă click dreapta pentru Asistentul AI."
                  className="w-full flex-1 min-h-[50vh] md:min-h-[600px] resize-none bg-transparent border-none outline-none focus:ring-0 text-slate-700 text-sm md:text-base leading-relaxed font-medium placeholder:text-slate-300 placeholder:italic"
                />
              </div>
            </div>
            
            {/* Status Bar */}
            <div className="h-8 bg-white border-t border-slate-200 flex items-center justify-between px-6 shrink-0 z-10">
              <span className="text-[10px] font-bold text-slate-400">
                {currentContent.length} caractere | {currentContent.split(/\s+/).filter(w => w.length > 0).length} cuvinte
              </span>
              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Salvat Automat
              </span>
            </div>

            {/* AI Generator Overlay Loading */}
            {isGenerating && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4 animate-bounce">
                  <Zap className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-black text-slate-900 italic tracking-tight">AI Redactează...</h3>
                <p className="text-sm font-medium text-slate-500 mt-2">LexSmart aplică rigoarea juridică.</p>
              </div>
            )}
          </>
        ) : (
          /* Empty State / Generator de Drafturi */
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-slate-50/50">
            <div className="w-24 h-24 bg-white rounded-3xl shadow-xl flex items-center justify-center mb-8 border border-slate-100">
              <FileText className="w-10 h-10 text-indigo-600" />
            </div>
            <h2 className="text-3xl font-black text-slate-900 italic mb-4">AiEditor Pro</h2>
            <p className="text-slate-500 max-w-md font-medium mb-12 leading-relaxed">
              Acest spațiu înlocuiește vechiul sistem de "Notițe" și "Drafturi". Acum, toate documentele tale interne beneficiază de salvare automată și asistență AI integrată.
            </p>

            <div className="w-full max-w-2xl bg-white p-8 rounded-[2.5rem] shadow-xl border border-slate-100">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6">Generare Rapidă AI</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {DRAFT_TYPES.map(type => (
                  <button
                    key={type.id}
                    onClick={() => handleGenerateTemplate(type.id)}
                    title={`Generează draft: ${type.label}`}
                    aria-label={`Generează draft: ${type.label}`}
                    className="flex flex-col items-center justify-center gap-3 p-4 rounded-2xl bg-slate-50 hover:bg-indigo-50 border border-slate-100 hover:border-indigo-200 transition-all group"
                  >
                    <span className="text-3xl group-hover:scale-110 transition-transform">{type.icon}</span>
                    <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider text-center">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Prompt Modal pentru Generare Document */}
      <PromptModal
        isOpen={promptModalConfig.isOpen}
        title={`Generare ${promptModalConfig.typeLabel}`}
        description={`Vă rugăm să oferiți un scurt context pentru a genera ${promptModalConfig.typeLabel.toLowerCase()} (ex: nume părți, obiectul contractului, detalii specifice).`}
        initialValue={promptModalConfig.initialContext}
        onClose={() => setPromptModalConfig(prev => ({ ...prev, isOpen: false }))}
        onSubmit={handlePromptSubmit}
      />
    </div>
  );
};

export default AiEditorView;
